<template>
    <!-- Popover BOWLING d'une carte catalogue — LIGNE SÈCHE à paliers d'effectif (décision
         produit 2026-09-18, inversion de la moitié POS de PLPRB-2652 : ni créneau, ni piste
         bloquée). Même chassis visuel que le popover d'options du VR (OptionsPopover.vue) :
         ancré à la carte, clampé au conteneur catalogue, fermé par le clic extérieur du
         Catalog. La logique est celle de l'ancien BookingModal, à l'identique : formule
         (prices_by_times) présélectionnée, stepper qui NAVIGUE la liste des effectifs vendables
         `party_sizes` (calculée SERVEUR — jamais un compteur libre), réelignage à chaque
         changement de formule, et le même contrat `confirm {prestationId, durationId,
         participants}`. AUCUN prix affiché : il dépend du PALIER d'effectif et le serveur le
         décide (parité exacte avec la réservation en ligne) — il revient dans le panier. -->
    <div class="cv3-popover" :style="popoverStyle" @click.stop>
        <!-- Fixed Header -->
        <div class="cv3-popover-header">
            <span class="cv3-popover-title" :title="title">{{ displayTitle }}</span>
        </div>

        <!-- Scrollable Options List : les FORMULES (durées / prices_by_times) -->
        <div class="cv3-popover-body">
            <button
                v-for="d in durations"
                :key="d.id"
                type="button"
                class="cv3-opt-row cv3-opt-row--single"
                :class="{ active: durationId === d.id }"
                @click="durationId = d.id"
            >
                <!-- Pastille de sélection (Radio dot) -->
                <span class="cv3-radio-dot" :class="{ checked: durationId === d.id }"></span>
                <div class="cv3-opt-info">
                    <!-- Libellé du gérant (« 1 partie »), sinon la durée formatée. Jamais le
                         prix : palier d'effectif → décidé serveur. -->
                    <div class="cv3-opt-name">{{ durationLabel(d) }}</div>
                </div>
            </button>
        </div>

        <!-- Fixed Footer : stepper des PARTICIPANTS sur la liste serveur -->
        <div class="cv3-popover-footer">
            <div class="cv3-footer-stepper" :class="{ 'cv3-footer-stepper--disabled': !selectedDuration }">
                <div class="cv3-footer-stepper-meta">
                    <span class="cv3-footer-stepper-qty-label">{{ $t('checkout.v3.booking_participants') }}</span>
                    <span class="cv3-footer-stepper-opt-name">
                        <!-- Rappel formule + durée EFFECTIVE du palier (6-7 pers → 2 h sur une
                             formule « 1 h ») : la donnée du serveur, jamais un calcul de paliers
                             côté client. -->
                        {{ stepperMeta || '-' }}
                    </span>
                </div>
                <div class="cv3-counter">
                    <!-- ± naviguent la LISTE des effectifs vendables (`party_sizes`, par
                         formule), jamais un compteur libre : vendre 3 sur un bowling réglé
                         2/4/6 partirait en refus serveur. Valeur en affichage seul — même
                         logique que l'ancien modal (pas de saisie libre). -->
                    <button
                        type="button"
                        class="cv3-counter-btn minus"
                        :disabled="!selectedDuration || !canDecrementPlayers"
                        @click="stepPlayers(-1)"
                    >
                        <MinusIcon :size="14" />
                    </button>
                    <span class="cv3-counter-val">{{ participants }}</span>
                    <button
                        type="button"
                        class="cv3-counter-btn plus"
                        :disabled="!selectedDuration || !canIncrementPlayers"
                        @click="stepPlayers(1)"
                    >
                        <PlusIcon :size="14" />
                    </button>
                </div>
            </div>

            <!-- Validation : rappel de l'EFFECTIF (jamais du prix — serveur). Toujours actif
                 dès qu'une formule existe : un refus serveur arrive en toast, pas en bouton mort. -->
            <button
                type="button"
                class="cv3-validate"
                :disabled="!canValidate"
                :class="{ 'cv3-validate--disabled': !canValidate }"
                @click="confirm"
            >
                {{ validateLabel }}
            </button>
        </div>
    </div>
</template>

<script>
import { Minus as MinusIcon, Plus as PlusIcon } from 'lucide-vue'

export default {
    name: 'BookingPopover',
    components: { MinusIcon, PlusIcon },
    props: {
        // La carte du catalogue (ou d'un résultat de recherche), telle que la rend le serveur :
        // elle porte `prices_by_times` ET `party_sizes` (effectifs vendables + durée effective,
        // par formule). Aucune autre donnée n'est nécessaire côté client — aucun fetch.
        prestation: { type: Object, required: true },
    },
    data() {
        const durations = this.prestation.prices_by_times || []
        const durationId = durations.length ? durations[0].id : null
        // Effectif initial : le PREMIER de la liste serveur de la formule présélectionnée — la
        // liste peut ne pas partir de 1 (ex. « 2,4,6 »), une initialisation à 1 serait hors bornes.
        const sizes = (this.prestation.party_sizes || {})[durationId] || []
        return {
            durationId,
            participants: sizes.length ? sizes[0].n : 1,
            windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
            popoverStyle: {},
        }
    },
    mounted() {
        window.addEventListener('resize', this.onResize)
        this.$nextTick(() => {
            this.adjustPosition()
        })
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
    },
    watch: {
        // Les effectifs vendables dépendent de la FORMULE (un palier peut n'exister que sur une
        // d'elles) : en changer réaligne l'effectif sur la nouvelle liste — pur état local, aucun
        // fetch. C'est la DONNÉE serveur qui décide ; le serveur revalide de toute façon à l'insert.
        durationId() {
            if (!this.allowedHeadcounts.includes(this.participants)) {
                this.participants = this.allowedHeadcounts[0]
            }
        },
    },
    computed: {
        title() {
            return this.prestation.name || ''
        },
        canValidate() {
            return !!this.durationId
        },
        durations() {
            return this.prestation.prices_by_times || []
        },
        /** La formule sélectionnée (objet de prices_by_times), null si aucune. */
        selectedDuration() {
            return this.durations.find((d) => d.id === this.durationId) || null
        },
        /** Les effectifs vendables de la formule courante — CALCULÉS SERVEUR (`party_sizes`,
            posés par catalogByTag/research). Null si le payload est périmé : repli ci-dessous. */
        partySizes() {
            return (this.prestation.party_sizes || {})[this.durationId] || null
        },
        /** Bornes du stepper : la liste serveur, ou l'effectif courant en repli (payload périmé
            sans `party_sizes`) — le stepper reste utilisable, le serveur tranche de toute façon. */
        allowedHeadcounts() {
            return this.partySizes ? this.partySizes.map((s) => s.n) : [this.participants]
        },
        /** Durée EFFECTIVE de la partie pour l'effectif courant : un palier peut la faire varier
            (6-7 pers → 2 h sur une formule « 1 h »). 0 = inconnue (payload périmé). */
        effectiveMinutes() {
            const size = this.partySizes && this.partySizes.find((s) => s.n === this.participants)
            return size ? Number(size.minutes) || 0 : 0
        },
        /** Rappel du footer : formule choisie · durée EFFECTIVE du palier. */
        stepperMeta() {
            if (!this.selectedDuration) return ''
            return this.effectiveMinutes
                ? `${this.durationLabel(this.selectedDuration)} · ${this.formatMinutes(this.effectiveMinutes)}`
                : this.durationLabel(this.selectedDuration)
        },
        /** Libellé du bouton : « Valider · 6 joueurs » — rappel d'EFFECTIF, jamais de prix
            (palier d'effectif → décidé serveur, il revient dans le panier). */
        validateLabel() {
            if (!this.selectedDuration) return this.$t('checkout.v3.booking_confirm')
            return `${this.$t('checkout.v3.booking_confirm')} · ${this.$tc('checkout.v3.booking_players', this.participants, {
                n: this.participants,
            })}`
        },
        displayTitle() {
            if (!this.title) return ''
            let maxChars = 20
            if (this.windowWidth > 600) {
                maxChars = 35
            } else if (this.windowWidth > 400) {
                maxChars = 25
            }
            if (this.title.length > maxChars) {
                return this.title.slice(0, maxChars) + '...'
            }
            return this.title
        },
        canDecrementPlayers() {
            return this.allowedHeadcounts.indexOf(this.participants) > 0
        },
        canIncrementPlayers() {
            const index = this.allowedHeadcounts.indexOf(this.participants)
            return index !== -1 && index < this.allowedHeadcounts.length - 1
        },
    },
    methods: {
        onResize() {
            this.windowWidth = window.innerWidth
            this.adjustPosition()
        },
        adjustPosition() {
            if (typeof window === 'undefined' || !this.$el || !this.$el.parentElement) return

            const parentRect = this.$el.parentElement.getBoundingClientRect()
            const margin = 12
            const popoverWidth = 290

            // Find catalog container boundaries, fallback to viewport
            const container = this.$el.closest('.cv3-catalog')
            let minLeft = margin
            let maxLeft = window.innerWidth - popoverWidth - margin

            if (container) {
                const containerRect = container.getBoundingClientRect()
                minLeft = containerRect.left + margin
                maxLeft = containerRect.right - popoverWidth - margin
            }

            // Centered target position in viewport coordinates
            let targetLeft = parentRect.left + parentRect.width / 2 - popoverWidth / 2

            // Clamp position within boundaries
            if (targetLeft < minLeft) {
                targetLeft = minLeft
            } else if (targetLeft > maxLeft) {
                targetLeft = maxLeft
            }

            // Convert to parent-relative coordinate
            const relativeLeft = targetLeft - parentRect.left

            this.popoverStyle = {
                left: `${relativeLeft}px`,
                transform: 'none',
            }
        },
        /** Un pas du stepper : +1/-1 dans la LISTE des effectifs vendables, jamais une valeur libre. */
        stepPlayers(direction) {
            const index = this.allowedHeadcounts.indexOf(this.participants)
            const next = this.allowedHeadcounts[index + direction]
            if (next !== undefined) this.participants = next
        },
        /** Libellé d'une formule : description du gérant, sinon durée formatée. */
        durationLabel(d) {
            if (d.price_duration_description) return d.price_duration_description
            return this.formatMinutes(Number(d.duration) || 0)
        },
        /** Des minutes en libellé court (« 45 min », « 1 h », « 1 h 30 »). */
        formatMinutes(minutes) {
            if (!minutes) return this.$t('checkout.v3.all_day')
            if (minutes < 60) return `${minutes} ${this.$t('checkout.v3.unit_m')}`
            const h = Math.floor(minutes / 60)
            const m = minutes % 60
            return m
                ? `${h}${this.$t('checkout.v3.unit_h')} ${m}${this.$t('checkout.v3.unit_m')}`
                : `${h}${this.$t('checkout.v3.unit_h')}`
        },
        /** Passer commande — AUCUN horaire, AUCUN prix : le serveur résout tout (durée du
            palier, prix à la parité exacte) et écrit la ligne sèche. Refus traduit en toast
            côté Catalog. Contrat identique à l'ancien BookingModal. */
        confirm() {
            if (!this.durationId) return
            this.$emit('confirm', {
                prestationId: this.prestation.id,
                durationId: this.durationId,
                participants: this.participants,
            })
        },
    },
}
</script>

<style scoped lang="scss">
/* Chassis dupliqué d'OptionsPopover.vue (décision 2026-09-18 : duplication complète plutôt
   que partial partagé — le composant VR reste intouché). Seuls écarts : la valeur du stepper
   est un affichage (pas une saisie) et les rangées ne portent pas de prix. */
.cv3-popover {
    position: absolute;
    top: 60%;
    left: 50%;
    transform: translateX(-50%);
    width: 290px;
    max-width: calc(100vw - 24px);
    z-index: 30;
    background: #161029;
    border: 1px solid rgba(171, 110, 220, 0.35);
    border-radius: 14px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
    overflow: hidden;
    font-family:
        'Poppins',
        system-ui,
        -apple-system,
        sans-serif;
    color: #fff;
    cursor: default;
}

.cv3-popover-header {
    display: flex;
    align-items: center;
    padding: 12px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: #1d1638;
}

.cv3-popover-title {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
}

.cv3-popover-body {
    flex: 1;
    min-height: 0;
    max-height: 220px;
    overflow-y: auto;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    /* Scrollbar styling */
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.12);
        border-radius: 999px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
}

.cv3-opt-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 10px;
    border-radius: 10px;
    background: #241c44;
    border: 1px solid transparent;
    color: #fff;
    text-align: left;
    transition:
        background 0.15s,
        border-color 0.15s;

    &.active {
        border-color: rgba(171, 110, 220, 0.75);
        background: #2e2352;
    }
}

.cv3-opt-row--single {
    width: 100%;
    cursor: pointer;
    justify-content: flex-start;
}

.cv3-radio-dot {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    border-radius: 999px;
    border: 2px solid rgba(255, 255, 255, 0.28);
    background: transparent;
    position: relative;
    transition:
        border-color 0.15s,
        background 0.15s;

    &.checked {
        border-color: #ab6edc;
        background: #7d2dbd;
        box-shadow: inset 0 0 0 2px #161029;
    }
}

.cv3-opt-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cv3-opt-name {
    font-size: 12px;
    font-weight: 500;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Stepper & counters */
.cv3-counter {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.cv3-counter-btn {
    width: 26px;
    height: 26px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-family: inherit;
    transition:
        background 0.15s,
        opacity 0.15s;

    &.minus {
        border: 1px solid rgba(255, 255, 255, 0.18);
        background: transparent;
        color: #c6d0dc;

        &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.08);
        }
        &:disabled {
            opacity: 0.3;
            cursor: not-allowed;
        }
    }

    &.plus {
        border: none;
        background: #7d2dbd;
        color: #fff;

        &:hover {
            background: #8f3ad2;
        }
    }
}

/* Valeur du stepper : AFFICHAGE SEUL (± naviguent la liste serveur — pas de saisie libre),
   d'où le centrage flex que l'input du popover VR tient nativement. */
.cv3-counter-val {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    width: 32px;
    height: 26px;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    background: rgba(125, 45, 189, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 10px;
    outline: none;
    padding: 0;
}

/* Footer layout */
.cv3-popover-footer {
    padding: 10px 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    background: #1d1638;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.cv3-footer-stepper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    background: #241c44;
    border: 1px solid rgba(171, 110, 220, 0.35);
    transition:
        opacity 0.15s,
        border-color 0.15s;
}

.cv3-footer-stepper--disabled {
    opacity: 0.5;
    border-color: rgba(255, 255, 255, 0.08);
}

.cv3-footer-stepper-meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.cv3-footer-stepper-qty-label {
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #889bb4;
}

.cv3-footer-stepper-opt-name {
    font-size: 12px;
    font-weight: 500;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
}

.cv3-validate {
    width: 100%;
    padding: 10px;
    border-radius: 10px;
    border: none;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition:
        background 0.15s,
        color 0.15s,
        opacity 0.15s;

    background: rgba(255, 255, 255, 0.06);
    color: #6b7480;

    &:not(:disabled) {
        background: #7d2dbd;
        color: #fff;

        &:hover {
            background: #8f3ad2;
        }
    }

    &:disabled,
    &.cv3-validate--disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
}
</style>
